/**
 * How far away a date is, in whole days and in words.
 *
 * A due date printed alone ("27.10.2026") makes the reader do the subtraction,
 * and the reader of an invoice is the one person who should not have to. These
 * two functions do it once, for every screen that shows a deadline.
 *
 * React-free and clock-free: `now` is passed in, so the test beside this file
 * needs neither a DOM nor a frozen system clock.
 */

const DAY_MS = 86_400_000;

/**
 * Whole CALENDAR days from `now` to the date: 0 is today, negative is past.
 *
 * Calendar days rather than elapsed time, because that is what a person means:
 * a deadline of tomorrow morning is "tomorrow" at 23:00 tonight, not "in 0
 * days". Both ends are read in the reader's own timezone, the same way
 * `fmtDate` prints them, so the number can never disagree with the date beside
 * it. `null` for a missing or unreadable date.
 */
export function daysUntil(iso: string | null | undefined, now: Date): number | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const target = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / DAY_MS);
}

/**
 * The same distance in words, in the reader's language: "in 14 days",
 * "15 days ago", "today", "tomorrow".
 *
 * `Intl.RelativeTimeFormat` rather than catalogue strings, because it already
 * knows every language's plural forms and its words for the days next to
 * today - a `{n} days` key cannot say "tomorrow", and it is wrong for one day
 * in most languages. A locale the runtime does not know falls back to English,
 * the same rule the catalogues follow. `null` when there is no date.
 */
export function relativeDay(iso: string | null | undefined, locale: string, now: Date): string | null {
  const days = daysUntil(iso, now);
  if (days === null) return null;
  for (const tag of [locale, "en"]) {
    try {
      return new Intl.RelativeTimeFormat(tag, { numeric: "auto" }).format(days, "day");
    } catch {
      // An unknown tag throws a RangeError; the next one in the list is tried.
    }
  }
  return null;
}
